import { useEffect, useState } from 'react';
import { centralApi, DemoBanner } from '@/services/centralApi';
import { Layout } from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Bot, User, Users, Clock, MessageCircle, TrendingUp, AlertTriangle, FileText } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export default function CentralDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waConnected, setWaConnected] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [d, s] = await Promise.all([
        centralApi.dashboard(),
        centralApi.getWaSettings().catch(() => ({})),
      ]);
      setData(d);
      setWaConnected(!!s?.connected);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <Layout><div className="text-muted-foreground text-sm">Carregando…</div></Layout>;
  if (!data) return <Layout><div /></Layout>;

  const c = data.cards;
  const waiting = c.aguardando_atendente || 0;

  const kpis = [
    { label: 'Conversas abertas', value: c.conversas_abertas, icon: MessageCircle, tone: 'text-blue-500' },
    { label: 'IA atendendo', value: c.ia_atendendo, icon: Bot, tone: 'text-emerald-500' },
    { label: 'Atendimento humano', value: c.atendimento_humano, icon: User, tone: 'text-indigo-500' },
    { label: 'Aguardando atendente', value: c.aguardando_atendente, icon: Users, tone: 'text-amber-500' },
  ];
  const kpis2 = [
    { label: 'Novas conversas hoje', value: c.novas_conversas_hoje, icon: TrendingUp, tone: 'text-blue-500' },
    { label: 'Solicitações de orçamento pela IA', value: c.orcamentos_ia, icon: FileText, tone: 'text-emerald-500' },
    { label: 'Tempo médio de resposta', value: `${Math.floor((c.tempo_medio_resposta_seg || 0) / 60)}m ${((c.tempo_medio_resposta_seg || 0) % 60)}s`, icon: Clock, tone: 'text-orange-500' },
  ];

  return (
    <Layout>
    <div className="space-y-6" data-testid="central-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Visão geral do atendimento no WhatsApp</p>
      </div>
      <DemoBanner when={!waConnected} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(k => {
          const Icon = k.icon;
          return (
            <Card key={k.label} className="border-border">
              <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">{k.label}</CardTitle>
                <Icon className={`h-4 w-4 ${k.tone}`} />
              </CardHeader>
              <CardContent><div className="text-2xl font-bold">{k.value}</div></CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {kpis2.map(k => {
          const Icon = k.icon;
          return (
            <Card key={k.label} className="border-border">
              <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">{k.label}</CardTitle>
                <Icon className={`h-4 w-4 ${k.tone}`} />
              </CardHeader>
              <CardContent><div className="text-2xl font-bold">{k.value}</div></CardContent>
            </Card>
          );
        })}
        <Card className={`border-2 ${waiting > 0 ? 'border-amber-500 bg-amber-500/10' : 'border-border'}`} data-testid="card-fila">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-amber-600 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Fila aguardando atendente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="text-xs text-muted-foreground">
              {waiting === 0 ? 'Nenhum cliente aguardando.' : `${waiting} cliente${waiting > 1 ? 's' : ''} aguardando atendimento humano.`}
            </div>
            <Link to="/central/aguardando">
              <Button variant={waiting > 0 ? 'default' : 'outline'} size="sm" className={waiting > 0 ? 'bg-amber-500 hover:bg-amber-500/90' : ''}>
                Abrir fila
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="border-border">
          <CardHeader><CardTitle className="text-base">Conversas e mensagens por dia</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <LineChart data={data.serie_dias}>
                <XAxis dataKey="label" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="conversas" stroke="#8b5a2b" strokeWidth={2} name="Conversas" />
                <Line type="monotone" dataKey="mensagens" stroke="#d69e2e" strokeWidth={2} name="Mensagens" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardHeader><CardTitle className="text-base">Transferências para humanos</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={data.serie_dias}>
                <XAxis dataKey="label" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Bar dataKey="handoffs" fill="#8b5a2b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border">
        <CardHeader><CardTitle className="text-base">Principais assuntos perguntados</CardTitle></CardHeader>
        <CardContent>
          {(!data.top_assuntos || data.top_assuntos.length === 0) ? (
            <p className="text-sm text-muted-foreground">Ainda sem dados. Assuntos aparecerão quando a IA classificar as conversas reais.</p>
          ) : (
            <div className="space-y-2">
              {data.top_assuntos.map(a => (
                <div key={a.label} className="flex items-center justify-between text-sm">
                  <span>{a.label}</span><span className="font-mono text-muted-foreground">{a.count}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
}
